import React from 'react';
import { useTranslation } from 'react-i18next';
import { Image } from '../Image';
import WhatsIncluded, { WhatsIncludedItem } from './WhatsIncluded';
import * as events from '../../lib/analytics/events';
import styles from './CartItemV2.module.scss';
import * as types from '../../types';
import FreeTrialLearnMoreModal from '../FreeTrialLearnMoreModal';
import useFreeTrialShippingPrice from '../../hooks/useFreeTrialShippingPrice';

export interface CartItemV2Props {
  cartItemId: types.CartItemID;
  productImageUrl: string;
  title: string;
  subtitle?: string;
  price?: string;
  originalPrice?: string;
  hardwareKitPrice?: string;
  hardwareKitOriginalPrice?: string;
  hardwareKitDiscountLabel?: string;
  membershipName?: string;
  membershipPrice?: string;
  membershipOriginalPrice?: string;
  /** Small line under the membership row, e.g. the rescue variant's "Included with your adoption". */
  membershipNote?: string;
  activationFee?: string;
  activationFeeWaived?: boolean;
  /** Small line under the activation fee row, e.g. "One-time, not part of your membership". */
  activationFeeNote?: string;
  isFreeTrialItem?: boolean;
  removeCartItem?: () => void;
  defaultExpanded?: boolean;
  includedItems?: WhatsIncludedItem[];
  subscriptionPrice?: string;
}

export default function CartItemV2({
  cartItemId,
  productImageUrl,
  title,
  subtitle,
  price,
  originalPrice,
  hardwareKitPrice,
  hardwareKitOriginalPrice,
  hardwareKitDiscountLabel,
  membershipName,
  membershipPrice,
  membershipOriginalPrice,
  membershipNote,
  activationFee,
  activationFeeWaived,
  activationFeeNote,
  isFreeTrialItem,
  removeCartItem,
  defaultExpanded = true,
  includedItems,
  subscriptionPrice,
}: CartItemV2Props) {
  const { t } = useTranslation();

  const shippingPrice = useFreeTrialShippingPrice();

  return (
    <div className={styles.cartItemV2} data-cart-item-id={cartItemId}>
      <div className={styles.header}>
        <div className={styles.imageContainer}>
          <Image className={styles.productImage} image={{ type: 'image', url: productImageUrl }} alt={title} />
        </div>

        <div className={styles.headerContent}>
          <div className={styles.titleRow}>
            <div className={styles.titleGroup}>
              <h3 className={styles.title}>{title}</h3>
              {subtitle && <p className={styles.subtitle}>{subtitle}</p>}
            </div>

            {price && (
              <div className={styles.priceGroup}>
                {originalPrice && <span className={styles.originalPrice}>{originalPrice}</span>}
                <span className={styles.priceMain}>{price}</span>
              </div>
            )}
          </div>

          {hardwareKitPrice && (
            <div className={styles.itemizedRow}>
              <span className={styles.itemizedRowLabel}>{t('hardware_kit')}</span>
              <span className={styles.itemizedRowPrice}>
                {hardwareKitOriginalPrice && <span className={styles.waivedPrice}>{hardwareKitOriginalPrice}</span>}
                {hardwareKitPrice}
              </span>
            </div>
          )}
          {hardwareKitDiscountLabel && <div className={styles.discountApplied}>{hardwareKitDiscountLabel}</div>}

          {membershipName && membershipPrice && (
            <div className={styles.itemizedRow}>
              <span className={styles.itemizedRowLabel}>{membershipName}</span>
              <span className={styles.itemizedRowPrice}>
                {membershipOriginalPrice && <span className={styles.waivedPrice}>{membershipOriginalPrice}</span>}
                {membershipPrice}
              </span>
            </div>
          )}
          {membershipName && membershipPrice && membershipNote && (
            <div className={styles.itemizedRowNote}>{membershipNote}</div>
          )}

          {activationFee && (
            <div className={styles.itemizedRow}>
              <span className={styles.itemizedRowLabel}>{t('one_time_activation_fee')}</span>
              <span className={styles.itemizedRowPrice}>
                {activationFeeWaived && <span className={styles.waivedPrice}>{activationFee}</span>}
                {activationFeeWaived ? t('cart_item_v2.price_free') : activationFee}
              </span>
            </div>
          )}
          {activationFee && activationFeeNote && <div className={styles.itemizedRowNote}>{activationFeeNote}</div>}

          {removeCartItem && (
            <button type="button" className={styles.removeLink} onClick={removeCartItem}>
              {t('cart_item_v2.remove')}
            </button>
          )}

          {isFreeTrialItem && (
            <p className={styles.disclaimer}>
              {t('cart_item_v2.trial_disclaimer')}{' '}
              {shippingPrice && subscriptionPrice ? (
                <FreeTrialLearnMoreModal
                  trigger={<span className={styles.learnMoreLink}>{t('cart_item_v2.learn_more')}</span>}
                  onOpen={() => events.freeTrialLearnMoreClickedFromCart()}
                  subscriptionPrice={subscriptionPrice}
                />
              ) : (
                <a
                  href="https://support.fitracking.com/hc/en-us/articles/48272655778579-Free-Trial-Program-Canada-Only"
                  target="_blank"
                  rel="noopener noreferrer"
                  className={styles.learnMoreLink}
                  onClick={() => events.freeTrialLearnMoreClickedFromCart()}
                >
                  {t('cart_item_v2.learn_more')}
                </a>
              )}
            </p>
          )}
        </div>
      </div>

      {includedItems && includedItems.length > 0 && (
        <WhatsIncluded items={includedItems} defaultExpanded={defaultExpanded} />
      )}
    </div>
  );
}
